import { QueryClient } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods } from '../constants/staticTypes';
import {
    getOrgSubscriptionPlansApi,
    getOrgInvoicesApi,
    getOrgSubscriptionDetailsApi,
    getOrgSubscriptionInfoApi,
    createOrgSubscriptionApi,
    changeOrgSubscriptionApi,
    cancelOrgSubscriptionApi,
    getOrgSubscriptionPortalApi,
} from '../constants/apiEndPoints';

// API Response Types
export interface ApiPrice {
    id: string;
    priceId: string;
    currency: string;
    amount: string;
    recurring: {
        meter?: string | null;
        interval: string;
        usage_type?: string;
        interval_count: number;
        trial_period_days?: number | null;
    };
    type: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    metadata: any;
    additionalData: any;
}

export interface ApiSubscriptionPlan {
    id: string;
    name: string;
    description: string;
    productId: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    metadata: any;
    additionalData: {
        marketing_features?: Array<{ name: string }>;
        [key: string]: any;
    };
    prices: ApiPrice[];
}

export interface ApiSubscriptionPlansResponse {
    code: string;
    message: string;
    data: ApiSubscriptionPlan[];
    details: any;
}

export interface ApiInvoice {
    invoice_id: string;
    status: string;
    amount_due: number;
    currency: string;
    period_start: string | null;
    period_end: string | null;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
    created_at: string | null;
}

export interface ApiInvoicesResponse {
    code: string;
    message: string;
    data: {
        data: ApiInvoice[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
        };
    };
    details: any;
}

export interface ApiOrganizationProductPlan {
    id: string;
    organizationId: string;
    planId: string;
    subscriptionId: string;
    stripeCustomerId: string;
    priceId: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    nextPeriodStart: string;
    nextPeriodEnd: string;
    metadata: any;
    createdAt: string;
    updatedAt: string;
    plan: ApiSubscriptionPlan;
}

export interface ApiOrgSubscriptionDetailsResponse {
    code: string;
    message: string;
    data: {
        subscriptionId: string;
        organizationProductPlan: ApiOrganizationProductPlan;
        addons: any[];
        onTrial: boolean;
        trialEndsAt: string | null;
        nextBillingDate: string;
        nextAmountPayable: number;
    };
    details: any;
}

// Types
export interface SubscriptionData {
    trialDaysLeft: number;
    planName: string;
    price: number;
    billingCycle: string;
    planStartDate: string;
    nextBillingDate: string;
    currentDay: number;
    totalDays: number;
    features: string[];
    paymentMethod: {
        type: string;
        lastFourDigits: string;
    };
    accountOwner: {
        name: string;
        role: string;
        status: string;
        avatar: string | null;
    };
    autoRenewal: boolean;
}

export interface SubscriptionPlan {
    id: number;
    planId: string;
    name: string;
    icon: string;
    description: string;
    price: number;
    monthlyPrice?: number;
    yearlyPrice?: number;
    monthlyPriceId: string;
    yearlyPriceId: string;
    features: string[];
    isCurrent?: boolean;
    isMostPopular?: boolean;
}

export interface FeatureComparison {
    feature: string;
    free: string | boolean;
    bronze: string | boolean;
    silver: string | boolean;
    gold: string | boolean;
}

// ─── Types for /subscription API response ──────────────────────────────────

export type _BillingLineItem = {
    lineId: string;
    priceId: string;
    productId: string;
    quantity: number;
    amount: number;
    amountInDollars: number;
    currency: string;
    description: string;
    periodStart: string;
    periodEnd: string;
};

export type _PlanMetadata = {
    planIconClass?: string;
    setAsRecommended?: string;
    [key: string]: string | undefined;
};

export type _PlanAdditionalData = {
    url: string | null;
    type: string;
    images: string[];
    object: string;
    livemode: boolean;
    metadata: _PlanMetadata;
    tax_code: string | null;
    shippable: boolean | null;
    attributes: string[];
    unit_label: string | null;
    default_price: string | null;
    marketing_features: Array<string | { name: string }>;
    package_dimensions: unknown | null;
    statement_descriptor: string | null;
};

export type _ApiPlanDetails = {
    id: string;
    name: string;
    description: string;
    productId: string;
    isAddon: boolean;
    isActive: boolean;
    metadata: _PlanMetadata;
    additionalData: _PlanAdditionalData;
};

export type _ApiPriceRecurring = {
    meter: string | null;
    interval: string;
    usage_type: string;
    interval_count: number;
    trial_period_days: number | null;
};

export type _ApiPriceAdditionalData = {
    object: string;
    product: string;
    livemode: boolean;
    nickname: string | null;
    lookup_key: string | null;
    tiers_mode: string | null;
    tax_behavior: string;
    billing_scheme: string;
    custom_unit_amount: unknown | null;
    transform_quantity: unknown | null;
    unit_amount_decimal: string;
};

export type _ApiPriceDetails = {
    id: string;
    priceId: string;
    planId: string;
    amount: string;
    currency: string;
    type: string;
    isAddon: boolean;
    recurring: _ApiPriceRecurring;
    isActive: boolean;
    metadata: Record<string, unknown>;
    additionalData: _ApiPriceAdditionalData;
};

export type _ApiOrganizationProductPlan = {
    id: string;
    organizationId: string;
    planId: string;
    subscriptionId: string;
    stripeCustomerId: string;
    priceId: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    subscriptionStartDate: string;
    featureResetAnchorDay: number;
    nextFeatureResetAt: string;
    nextPeriodStart: string | null;
    nextPeriodEnd: string | null;
    graceStartedAt: string | null;
    nextPaymentFailureReason: string | null;
    metadata: {
        latestInvoiceId?: string;
        [key: string]: string | undefined;
    };
    status: string;
    createdAt: string;
    updatedAt: string;
    plan: _ApiPlanDetails;
    price: _ApiPriceDetails | null;
};

export type _OrgSubscriptionDetailsData = {
    subscriptionId: string;
    organizationProductPlan: _ApiOrganizationProductPlan;
    addons: unknown[];
    currentPeriodStart: string;
    currentPeriodEnd: string;
    onTrial: boolean;
    trialEndsAt: string | null;
    nextBillingDate: string;
    nextAmountPayableInDollars: number;
    currentItems: _BillingLineItem[];
    nextPeriodItems: _BillingLineItem[];
    upcomingInvoiceId: string;
    upcomingInvoiceStatus: string;
    cancelAtPeriodEnd: boolean;
    markedAsCanceled: boolean;
};

export type _OrgSubscriptionDetailsResponse = {
    code: string;
    message: string;
    data: _OrgSubscriptionDetailsData;
    details: unknown | null;
};

// ─── Types for /subscription-info API response ─────────────────────────────

export type _SubscriptionInfoPlanDetails = {
    id: string;
    name: string;
    description: string;
    productId: string;
    isAddon: boolean;
};

export type _SubscriptionInfoOrgProductPlan = {
    id: string;
    planId: string;
    subscriptionId: string;
    stripeCustomerId: string;
    priceId: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    subscriptionStartDate: string;
    featureResetAnchorDay: number;
    nextFeatureResetAt: string;
    nextPeriodStart: string | null;
    nextPeriodEnd: string | null;
    plan: _SubscriptionInfoPlanDetails;
    price: _ApiPriceDetails | null;
};

export type _PlanFeatureOption = {
    id: string;
    optionName: string;
    optionDescription: string;
    optionUnitList: string[];
};

export type _PlanFeatureInfo = {
    id: string;
    name: string;
    description: string | null;
    featureKey: string;
    lookupKey: string;
};

export type _PlanFeatureEntry = {
    id: string;
    planId: string;
    featureId: string;
    featureOptionId: string | null;
    limitValue: unknown | null;
    limitUnit: string;
    isUnlimited: boolean;
    feature: _PlanFeatureInfo;
    featureOption: _PlanFeatureOption | null;
};

export type _PlanFeatures = {
    plan: {
        planId: string;
        features: _PlanFeatureEntry[];
    };
    addons: unknown[];
};

export type _PaymentDetails = {
    hasPaymentMethod: boolean;
    paymentMethodUpdateUrl: string;
};

export type _OrgSubscriptionInfoData = {
    hasSubscription: boolean;
    subscriptionId: string;
    tenantLimit: number;
    plan: _SubscriptionInfoOrgProductPlan;
    addons: unknown[];
    planFeatures: _PlanFeatures;
    paymentDetails: _PaymentDetails;
    isTrialEligible?: boolean;
};

export type _OrgSubscriptionInfoResponse = {
    code: string;
    message: string;
    data: _OrgSubscriptionInfoData;
    details: unknown | null;
};

// ─── Cancel subscription payload ───────────────────────────────────────────

export type _CancelSubscriptionPayload = {
    id: string;
    name: string;
    description: string;
    isAddon: boolean;
    sourceType: string;
    sourceId: string;
    isActive: boolean;
    metadata: Record<string, unknown>;
};

// Utility functions
export const calculateProgress = (currentDay: number, totalDays: number): number => {
    if (totalDays === 0) return 0;
    return Math.round((currentDay / totalDays) * 100);
};

export const formatPrice = (price: number, isYearly: boolean = false): string => {
    if (price === 0) return 'Free';

    if (isYearly) {
        const yearlyPrice = price * 12 * 0.83; // 17% discount
        return `$${yearlyPrice.toFixed(2)}`;
    }

    return `$${price}`;
};

export const calculateYearlyPrice = (monthlyPrice: number): number => {
    return monthlyPrice * 12 * 0.83; // 17% discount
};

// API Functions
export const getOrgSubscriptionPlans = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getOrgSubscriptionPlansApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export const getOrgSubscriptionInfo = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getOrgSubscriptionInfoApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export const createOrgSubscription = (queryClient: QueryClient, planId: string, priceId: string) => {
    return HttpServiceHelper({
        url: createOrgSubscriptionApi,
        method: _HttpMethods.POST,
        queryClient,
        data: {
            plan: {
                id: planId,
                priceId: priceId,
            },
        },
    });
};

// Transform API response to component format
export const transformApiPlansToComponentFormat = (apiPlans: ApiSubscriptionPlan[], currentPlanId?: string): SubscriptionPlan[] => {
    return apiPlans
        .filter((plan) => plan.isActive && plan.prices.length > 0)
        .map((plan, index) => {
            // Find monthly price (interval_count = 1, interval = 'month')
            // Try active first, then fall back to any monthly price
            const monthlyPriceObj =
                plan.prices.find((p) => p.isActive && p.recurring?.interval === 'month' && p.recurring?.interval_count === 1) ||
                plan.prices.find((p) => p.recurring?.interval === 'month' && p.recurring?.interval_count === 1);

            // Find yearly price (interval_count = 1, interval = 'year')
            // Try active first, then fall back to any yearly price
            const yearlyPriceObj =
                plan.prices.find((p) => p.isActive && p.recurring?.interval === 'year' && p.recurring?.interval_count === 1) ||
                plan.prices.find((p) => p.recurring?.interval === 'year' && p.recurring?.interval_count === 1);

            // Extract features from marketing_features
            // Handle both string arrays and object arrays
            const rawFeatures = plan.additionalData?.marketing_features || [];
            const features = rawFeatures
                .map((f: any) => {
                    // If it's a string, return it directly
                    if (typeof f === 'string') return f;
                    // If it's an object with name property, return the name
                    if (f && typeof f === 'object' && f.name) return f.name;
                    // Otherwise return null
                    return null;
                })
                .filter((name: string | null) => name && name.trim() !== '') as string[];

            // Determine icon based on metadata or index
            const iconFromMetadata = plan.metadata?.planIconClass || plan.additionalData?.metadata?.planIconClass;
            const defaultIcons = ['ri-gift-line', 'ri-star-line', 'ri-flashlight-line', 'ri-vip-crown-line'];
            const icon = iconFromMetadata || defaultIcons[index % defaultIcons.length];

            // Convert prices from string to number
            const monthlyPrice = monthlyPriceObj ? parseFloat(monthlyPriceObj.amount) : 0;
            const yearlyPrice = yearlyPriceObj ? parseFloat(yearlyPriceObj.amount) : 0;

            return {
                id: index + 1,
                planId: plan.id, // Original plan ID from API
                name: plan.name,
                icon: icon,
                description: plan.description,
                price: monthlyPrice, // Default to monthly price
                monthlyPrice: monthlyPrice,
                yearlyPrice: yearlyPrice,
                monthlyPriceId: monthlyPriceObj?.priceId || '', // Stripe price ID for monthly
                yearlyPriceId: yearlyPriceObj?.priceId || '', // Stripe price ID for yearly
                features: features,
                isCurrent: currentPlanId === plan.id,
                isMostPopular: plan.metadata?.setAsRecommended === 'true' || plan.additionalData?.metadata?.setAsRecommended === 'true',
            };
        });
};

// Fetch organization invoices
export const getOrgInvoices = (queryClient: QueryClient, page: number = 1, limit: number = 20) => {
    return HttpServiceHelper({
        url: `${getOrgInvoicesApi}?page=${page}&limit=${limit}`,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// guards the page loop against a pagination object that never ends
const MAX_INVOICE_PAGES = 50;

// Fetch every invoice page and return them newest first (the year filter needs the full history)
export const getAllOrgInvoices = async (queryClient: QueryClient): Promise<ApiInvoice[]> => {
    const invoices: ApiInvoice[] = [];
    let page = 1;
    let totalPages = 1;
    do {
        const response = await getOrgInvoices(queryClient, page);
        const body = response?.data as ApiInvoicesResponse | undefined;
        invoices.push(...(body?.data?.data ?? []));
        totalPages = body?.data?.pagination?.totalPages ?? 1;
        page += 1;
    } while (page <= totalPages && page <= MAX_INVOICE_PAGES);

    const createdAt = (invoice: ApiInvoice) => (invoice.created_at ? new Date(invoice.created_at).getTime() : 0);
    return invoices.sort((a, b) => createdAt(b) - createdAt(a));
};

export const changeOrgSubscription = (queryClient: QueryClient, planId: string, priceId: string) => {
    return HttpServiceHelper({
        url: changeOrgSubscriptionApi,
        method: _HttpMethods.POST,
        data: {
            plan: {
                id: planId,
                priceId,
            },
        },
        queryClient,
    });
};

// Fetch organization subscription details
export const getOrgSubscriptionDetails = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getOrgSubscriptionDetailsApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// Cancel organization subscription
export const cancelOrgSubscription = (queryClient: QueryClient, data: _CancelSubscriptionPayload) => {
    return HttpServiceHelper({
        url: cancelOrgSubscriptionApi,
        method: _HttpMethods.POST,
        data,
        queryClient,
    });
};

// Fetch organization billing portal URL
export const getOrgSubscriptionPortal = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getOrgSubscriptionPortalApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};
